import { useRef, useState } from 'react'
import { ActionIcon, Anchor, Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconDownload, IconFile, IconPaperclip, IconTrash } from '@/components/icons'
import { api, ApiError } from '@/lib/api'
import { resolveUploadsUrl } from '@/lib/uploads'
import type { SupportKnowledgeAttachmentRow } from '@/types/api'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type Props = {
  articleId?: string | null
  attachments: SupportKnowledgeAttachmentRow[]
  pendingFiles: File[]
  canEdit: boolean
  onAttachmentsChange: (items: SupportKnowledgeAttachmentRow[]) => void
  onPendingChange: (files: File[]) => void
}

export function KnowledgeArticleAttachments({
  articleId,
  attachments,
  pendingFiles,
  canEdit,
  onAttachmentsChange,
  onPendingChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const pickFiles = () => inputRef.current?.click()

  const addPending = (files: FileList | null) => {
    if (!files?.length) return
    const next = [...pendingFiles]
    for (const file of Array.from(files)) {
      if (file.size > 25 * 1024 * 1024) {
        showNotification({ title: 'Файл', message: `${file.name}: больше 25 МБ`, color: 'yellow' })
        continue
      }
      next.push(file)
    }
    onPendingChange(next)
    if (inputRef.current) inputRef.current.value = ''
  }

  const uploadOne = async (file: File) => {
    if (!articleId) {
      onPendingChange([...pendingFiles, file])
      return
    }
    setUploading(true)
    try {
      const row = await api.supportKnowledgeUploadAttachment(articleId, file)
      onAttachmentsChange([...attachments, row])
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить файл',
        color: 'red',
      })
    } finally {
      setUploading(false)
    }
  }

  const removePending = (index: number) => {
    onPendingChange(pendingFiles.filter((_, i) => i !== index))
  }

  const deleteAttachment = (item: SupportKnowledgeAttachmentRow) => {
    openConfirmModal({
      title: 'Удалить файл?',
      children: <Text size="sm">«{item.original_filename}» будет удалён из материала.</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.supportKnowledgeDeleteAttachment(item.id)
          onAttachmentsChange(attachments.filter((a) => a.id !== item.id))
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          Файлы
        </Text>
        {canEdit && (
          <Button
            size="compact-xs"
            variant="light"
            leftSection={<IconPaperclip size={14} stroke={1.5} />}
            loading={uploading}
            onClick={pickFiles}
          >
            Прикрепить
          </Button>
        )}
      </Group>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.md,.json"
        onChange={(e) => {
          const files = e.target.files
          if (articleId) {
            void (async () => {
              for (const file of Array.from(files || [])) {
                await uploadOne(file)
              }
              if (inputRef.current) inputRef.current.value = ''
            })()
          } else {
            addPending(files)
          }
        }}
      />
      {attachments.length === 0 && pendingFiles.length === 0 ? (
        <Text size="xs" c="dimmed">
          PDF, Word, Excel, архивы и другие документы до 25 МБ
        </Text>
      ) : (
        <Stack gap={6}>
          {attachments.map((item) => (
            <Group key={item.id} justify="space-between" wrap="nowrap" gap="xs">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                <IconFile size={16} stroke={1.5} />
                <Box style={{ minWidth: 0 }}>
                  <Anchor
                    href={resolveUploadsUrl(item.file_path || item.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    lineClamp={1}
                  >
                    {item.original_filename}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    {formatFileSize(item.file_size)}
                  </Text>
                </Box>
              </Group>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Скачать">
                  <ActionIcon
                    component="a"
                    href={resolveUploadsUrl(item.file_path || item.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                  >
                    <IconDownload size={16} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                {canEdit && (
                  <Tooltip label="Удалить">
                    <ActionIcon variant="subtle" color="red" onClick={() => deleteAttachment(item)}>
                      <IconTrash size={16} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
          ))}
          {pendingFiles.map((file, index) => (
            <Group key={`${file.name}-${index}`} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <IconFile size={16} stroke={1.5} />
                <Box style={{ minWidth: 0 }}>
                  <Text size="sm" lineClamp={1}>
                    {file.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatFileSize(file.size)} · будет загружен при сохранении
                  </Text>
                </Box>
              </Group>
              {canEdit && (
                <ActionIcon variant="subtle" color="red" onClick={() => removePending(index)}>
                  <IconTrash size={16} stroke={1.5} />
                </ActionIcon>
              )}
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  )
}

export async function flushPendingKnowledgeAttachments(
  articleId: string,
  pendingFiles: File[],
): Promise<SupportKnowledgeAttachmentRow[]> {
  const uploaded: SupportKnowledgeAttachmentRow[] = []
  for (const file of pendingFiles) {
    uploaded.push(await api.supportKnowledgeUploadAttachment(articleId, file))
  }
  return uploaded
}
